import CryptoJS from "crypto-js";

/**
 * 获取访问密钥，支持 SSR 环境
 */
function getAccessKey(): string {
  if (typeof localStorage !== "undefined") {
    return localStorage.getItem("YZYAK") || "";
  }
  return "";
}

/**
 * 获取签名密钥，支持 SSR 环境
 */
function getSecretKey(): string {
  if (typeof localStorage !== "undefined") {
    return localStorage.getItem("YZYSK") || "";
  }
  return "";
}

/**
 * 签名函数
 * @param headers 请求头参数
 * @param params 请求参数
 * @param sk 签名密钥
 * @returns string 签名结果
 */
export function sign(
  headers: Record<string, string | number>,
  params: Record<string, any>,
  sk: string = getSecretKey(),
): string {
  if (!sk) {
    console.warn(
      "[tzj-player] 签名密钥 YZYSK 为空，签名结果将不被服务端认可，请确认已写入 localStorage",
    );
  }
  const obj = Object.assign({}, headers, params);
  const keys = Object.keys(obj).sort();
  let str = "";
  // 拼接字符串
  keys.forEach((key, index) => {
    str += `${key}="${obj[key]}"`;
    if (index !== keys.length - 1) {
      str += "&";
    }
  });
  console.log("未加签的字符串", str);
  // 加密
  const hash = CryptoJS.HmacSHA256(str, sk);
  console.log("加签后的字符串", hash.toString(CryptoJS.enc.Hex));
  return hash.toString(CryptoJS.enc.Hex);
}
/**
 * 获取云智眼设备通道ID
 * @param deviceId 设备ID
 * @returns Promise<string | false> 成功返回通道ID，失败返回false
 */
export async function GetYunZhiYanDeviceChannel(
  deviceId: string,
): Promise<string | false> {
  try {
    const headers: Record<string, string | number> = {
      algorithm: "HmacSHA256",
      requestTime: Date.now(),
      accessKey: getAccessKey(),
    };
    const params = { deviceId };
    headers.sign = sign(headers, params);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10秒超时

    const response = await fetch("/vsuap-platform-api/v1/device/queryChannel", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body: JSON.stringify(params),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error(`HTTP错误: ${response.status} ${response.statusText}`);
      return false;
    }

    const res = await response.json();
    const { code, data } = res;

    if (
      code === 200 &&
      Array.isArray(data) &&
      data.length > 0 &&
      data[0].channelCodeId
    ) {
      return data[0].channelCodeId || "";
    } else {
      console.error(
        `业务错误: code=${code}, message=${res.message || "未知错误"}`,
      );
      return false;
    }
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === "AbortError") {
        console.error("请求超时: GetYunZhiYanDeviceChannel");
      } else if (error.name === "TypeError") {
        console.error("网络错误: GetYunZhiYanDeviceChannel", error.message);
      } else {
        console.error(
          "JSON解析错误或其他错误: GetYunZhiYanDeviceChannel",
          error.message,
        );
      }
    } else {
      console.error("未知错误: GetYunZhiYanDeviceChannel", error);
    }
    return false;
  }
}

/**
 * 获取云智眼设备视频播放地址
 * @param deviceId 设备ID
 * @param channelCodeId 通道ID
 * @returns Promise<string | false> 成功返回视频播放地址，失败返回false
 */
export async function GetYunZhiYanDeviceVideoUrl(
  deviceId: string,
  channelCodeId: string,
): Promise<string | false> {
  try {
    const headers: Record<string, string | number> = {
      algorithm: "HmacSHA256",
      requestTime: Date.now(),
      accessKey: getAccessKey(),
    };
    const params = { deviceId, channelCodeId };
    headers.sign = sign(headers, params);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10秒超时

    const response = await fetch("/vsuap-platform-api/v1/device/playOn", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body: JSON.stringify(params),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error(`HTTP错误: ${response.status} ${response.statusText}`);
      return false;
    }

    const res = await response.json();
    const { code, data } = res;

    if (code === 200) {
      return data?.flv || "";
    } else {
      console.error(
        `业务错误: code=${code}, message=${res.message || "未知错误"}`,
      );
      return false;
    }
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === "AbortError") {
        console.error("请求超时: GetYunZhiYanDeviceVideoUrl");
      } else if (error.name === "TypeError") {
        console.error("网络错误: GetYunZhiYanDeviceVideoUrl", error.message);
      } else {
        console.error(
          "JSON解析错误或其他错误: GetYunZhiYanDeviceVideoUrl",
          error.message,
        );
      }
    } else {
      console.error("未知错误: GetYunZhiYanDeviceVideoUrl", error);
    }
    return false;
  }
}

/**
 * 从cookie读取 authorized‑token，并解析出 accessToken
 */
export function parseAuthorizedTokenCookie() {
  // 1. 取出cookie字符串
  const match = document.cookie.match(/authorized-token=([^;]+)/);
  if (!match) return null;

  // 2. 先 decodeURIComponent 解码url编码
  const rawStr = decodeURIComponent(match[1]);
  try {
    // 3. 解析json对象
    const obj = JSON.parse(rawStr);
    return obj.accessToken; // 拿到真正的token字符串
  } catch (e) {
    console.error("解析authorized‑token失败", e);
    return null;
  }
}

export async function getVideoUrlByCamreaId(deviceId?: string) {
  console.log("🚀 ~ deviceId:", deviceId);
  return await fetch("http://192.168.111.212:8099/system/user/getInfo", {
    method: "get",
    headers: {
      authorization: parseAuthorizedTokenCookie(),
    },
  })
    .then((r) => r.json())
    .then((data) => {
      console.log("🚀 ~ data:", data);
      return data.code;
    });
}
